/**
 * SEO Configuration
 * 
 * Centralized SEO settings and utilities for the entire site.
 */

import Config from './Config';

export const SEO = {
  // Site defaults
  siteName: Config.name,
  siteUrl: 'https://diegogarcia.ca',
  defaultTitle: `${Config.name} - Full-Stack Software Engineer`,
  defaultDescription: 
    'Experienced Full-Stack Software Engineer focused on building scalable, optimized and performant solutions. 20+ years of experience in .NET, React, Flutter, and cloud technologies.',
  defaultImage: '/og-image.png',
  twitterHandle: '@DevDiegoGarcia',
  
  // Keywords for the site
  keywords: [
    'Software Engineer',
    'Full-Stack Developer',
    '.NET Developer',
    'React Developer',
    'Flutter Developer',
    'System Architecture',
    'Microservices',
    'Cloud Computing',
    'Azure',
    'Technical Writer',
    'Design Patterns',
    'Software Architecture',
    'Kingston Ontario',
    'Remote Developer',
  ],
  
  // Page-specific SEO
  pages: {
    home: {
      title: `${Config.name} - Full-Stack Software Engineer & Architect`,
      description: 
        'I love to build scalable, optimized and performant solutions for companies. 20+ years of experience in .NET, React, Flutter, and cloud technologies.',
    },
    blog: {
      title: `Blog | ${Config.name}`,
      description: 
        'Thoughts on software design, patterns, and architecture. Deep dives into system design, best practices, and engineering culture.',
    },
    resume: {
      title: `Resume | ${Config.name}`,
      description: 
        'Full-Stack Software Engineer with 20+ years of experience. Expertise in .NET, React, Flutter, microservices, and cloud architecture.',
    },
  },
};

/**
 * Generate structured data (JSON-LD) for a person
 */
export const generatePersonSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Person',
  name: Config.name,
  jobTitle: 'Full-Stack Software Engineer',
  description: SEO.defaultDescription,
  url: SEO.siteUrl,
  sameAs: [
    Config.socialMediaUrl?.linkedin,
    Config.socialMediaUrl?.github,
    Config.socialMediaUrl?.x,
    Config.socialMediaUrl?.instagram,
  ].filter(Boolean),
  image: `${SEO.siteUrl}/og-image.png`,
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Kingston',
    addressRegion: 'ON',
    addressCountry: 'CA',
  },
});

/**
 * Generate structured data for a blog post
 */
export const generateBlogPostSchema = (post) => ({
  '@context': 'https://schema.org',
  '@type': 'BlogPosting',
  headline: post.title,
  description: post.description,
  author: {
    '@type': 'Person',
    name: post.author,
    url: SEO.siteUrl,
  },
  datePublished: post.date instanceof Date ? post.date.toISOString() : post.date,
  dateModified: post.date instanceof Date ? post.date.toISOString() : post.date,
  url: `${SEO.siteUrl}/blog/${post.slug}`,
  image: post.thumbnail || `${SEO.siteUrl}/og-image.png`,
  keywords: post.tags?.join(', '),
  mainEntityOfPage: {
    '@type': 'WebPage',
    '@id': `${SEO.siteUrl}/blog/${post.slug}`,
  },
});

/**
 * Generate structured data for the blog listing page
 */
export const generateBlogSchema = (posts) => ({
  '@context': 'https://schema.org',
  '@type': 'Blog',
  name: `${Config.name}'s Blog`,
  description: SEO.pages.blog.description,
  url: `${SEO.siteUrl}/blog`,
  blogPost: posts.map(post => ({
    '@type': 'BlogPosting',
    headline: post.title,
    datePublished: post.date instanceof Date ? post.date.toISOString() : post.date,
    url: `${SEO.siteUrl}/blog/${post.slug}`,
  })),
});

export default SEO;
